import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, PlayIcon, StopIcon,
  EyeIcon, DocumentTextIcon, PaperAirplaneIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const curYear = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';
const STATUS_COLOR = { draft:'#9094a8', active:'#10c98e', closed:'#ef4444' };
const STATUS_BG    = { draft:'#f0f2f7', active:'#ecfdf5', closed:'#fef2f2' };

const DEFAULT_SUBJECT_QUESTIONS = [
  { text:'The teacher explains concepts clearly',              type:'rating', required:true },
  { text:'The teacher is punctual and regular',               type:'rating', required:true },
  { text:'The teacher encourages students to ask questions',  type:'rating', required:true },
  { text:'The teacher gives helpful feedback on assignments', type:'rating', required:true },
  { text:'Overall I am satisfied with this teacher',         type:'rating', required:true },
  { text:'Any additional comments',                          type:'text',   required:false },
];
const DEFAULT_CT_QUESTIONS = [
  { text:'The class teacher maintains a positive classroom environment', type:'rating', required:true },
  { text:'The class teacher is approachable and supportive',            type:'rating', required:true },
  { text:'The class teacher communicates well with students',           type:'rating', required:true },
  { text:'Overall I am satisfied with my class teacher',               type:'rating', required:true },
  { text:'Any additional comments',                                    type:'text',   required:false },
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .af * { box-sizing:border-box; }
  .af { font-family:'Sora',sans-serif; color:#1a1d2e; animation:afFade .4s ease both; }
  @keyframes afFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .af-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .af-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .af-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue   { background:#3b6ef8; color:white; } .btn-blue:hover   { background:#2d5ef0; }
  .btn-green  { background:#10c98e; color:white; } .btn-green:hover  { background:#0db87e; }
  .btn-teal   { background:#0d9488; color:white; } .btn-teal:hover   { background:#0f766e; }
  .btn-amber  { background:#fffbeb; color:#f59e0b; border:1px solid #fde68a; } .btn-amber:hover { background:#f59e0b; color:white; }
  .btn-red    { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover   { background:#ef4444; color:white; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover  { background:#e4e7f0; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .forms-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(340px,1fr)); gap:1rem; }
  .form-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem; transition:box-shadow .15s; }
  .form-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.07); }
  .fc-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:.8rem; gap:8px; }
  .fc-title { font-size:.9rem; font-weight:700; color:#0f1123; }
  .fc-year  { font-size:.7rem; color:#9094a8; margin-top:2px; font-family:'JetBrains Mono',monospace; }
  .status-pill { display:inline-flex; font-size:.67rem; font-weight:700; padding:3px 10px; border-radius:99px; white-space:nowrap; text-transform:capitalize; flex-shrink:0; }
  .fc-meta { display:flex; gap:12px; font-size:.72rem; color:#9094a8; margin-bottom:.9rem; flex-wrap:wrap; }
  .fc-stat { font-weight:600; color:#0f1123; }
  .progress-bar { height:6px; background:#f0f2f8; border-radius:99px; overflow:hidden; margin-bottom:.9rem; }
  .progress-fill { height:100%; border-radius:99px; background:#3b6ef8; transition:width .4s ease; }
  .fc-actions { display:flex; gap:6px; flex-wrap:wrap; }
  .af-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; background:white; border-radius:16px; border:1px solid #e8ebf4; }
  .af-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  /* ── Report Modal ── */
  .af-backdrop { position:fixed; top:0; left:0; width:100vw; height:100vh; background:rgba(15,17,35,.65); backdrop-filter:blur(6px); display:flex; align-items:flex-start; justify-content:center; z-index:99999; overflow-y:auto; padding:24px 20px; }
  .af-modal { background:white; border-radius:20px; padding:28px; width:780px; max-width:calc(100vw - 40px); box-shadow:0 24px 64px rgba(0,0,0,.3); animation:moIn .25s ease; margin:auto; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; flex-wrap:wrap; gap:8px; }
  .modal-title { font-size:1.05rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  /* ── Teacher section ── */
  .teacher-section { background:#fafbff; border-radius:14px; border:1px solid #e8ebf4; margin-bottom:16px; overflow:hidden; }
  .ts-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .ts-name { font-size:.92rem; font-weight:700; color:#0f1123; }
  .ts-meta { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .ts-score { display:flex; align-items:center; gap:6px; }
  .score-ring { display:flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; border:3px solid; font-size:.82rem; font-weight:700; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
  .ts-sent-badge { font-size:.66rem; font-weight:700; padding:3px 9px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .ts-body { padding:16px 18px; }
  /* ── Eval type tabs ── */
  .eval-tabs { display:flex; gap:6px; margin-bottom:14px; flex-wrap:wrap; }
  .eval-tab { padding:6px 14px; border-radius:99px; border:1.5px solid #e0e4f0; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; background:white; color:#6b7194; transition:.15s; }
  .eval-tab.active-subject { background:#eef2ff; color:#3b6ef8; border-color:#3b6ef8; }
  .eval-tab.active-class_teacher { background:#f0fdfa; color:#0d9488; border-color:#0d9488; }
  /* ── Question result ── */
  .q-result { margin-bottom:14px; padding-bottom:14px; border-bottom:1px solid #f0f2f8; }
  .q-result:last-child { border-bottom:none; margin-bottom:0; padding-bottom:0; }
  .q-result-text { font-size:.8rem; font-weight:600; color:#0f1123; margin-bottom:8px; line-height:1.5; }
  /* ── Rating graph ── */
  .rating-wrap { display:flex; align-items:flex-start; gap:16px; flex-wrap:wrap; }
  .avg-block { text-align:center; flex-shrink:0; }
  .avg-num { font-size:2rem; font-weight:700; font-family:'JetBrains Mono',monospace; line-height:1; }
  .avg-label { font-size:.66rem; color:#9094a8; margin-top:3px; font-weight:600; }
  .stars-row { display:flex; gap:2px; justify-content:center; margin-top:4px; }
  .star-svg { width:14px; height:14px; }
  .resp-label { font-size:.68rem; color:#9094a8; margin-top:3px; }
  .rating-bars { flex:1; min-width:160px; }
  .rb-row { display:flex; align-items:center; gap:6px; margin-bottom:3px; }
  .rb-label { width:14px; font-size:.68rem; color:#9094a8; font-family:'JetBrains Mono',monospace; text-align:right; flex-shrink:0; }
  .rb-track { flex:1; height:10px; background:#f0f2f8; border-radius:99px; overflow:hidden; }
  .rb-fill  { height:100%; border-radius:99px; transition:width .5s ease; }
  .rb-count { width:22px; font-size:.66rem; color:#9094a8; font-family:'JetBrains Mono',monospace; flex-shrink:0; }
  /* improvement tag */
  .improve-tag { display:inline-flex; align-items:center; gap:4px; font-size:.66rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#fef2f2; color:#ef4444; margin-left:6px; }
  .good-tag    { display:inline-flex; align-items:center; gap:4px; font-size:.66rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#ecfdf5; color:#10c98e; margin-left:6px; }
  /* yes/no */
  .yn-display { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
  .yn-bar-wrap { flex:1; min-width:120px; }
  .yn-row { display:flex; align-items:center; gap:6px; margin-bottom:4px; font-size:.74rem; }
  .yn-label { width:28px; font-weight:700; }
  .yn-yes-label { color:#10c98e; } .yn-no-label { color:#ef4444; }
  .yn-track { flex:1; height:8px; background:#f0f2f8; border-radius:99px; overflow:hidden; }
  .yn-fill-yes { height:100%; background:#10c98e; border-radius:99px; transition:width .5s; }
  .yn-fill-no  { height:100%; background:#ef4444; border-radius:99px; transition:width .5s; }
  .yn-count { width:24px; font-size:.68rem; color:#9094a8; font-family:'JetBrains Mono',monospace; }
  /* comments */
  .comments-list { display:flex; flex-direction:column; gap:5px; }
  .comment-item { background:white; border:1px solid #e8ebf4; border-radius:8px; padding:8px 12px; font-size:.76rem; color:#1a1d2e; font-style:italic; line-height:1.5; }
  /* create form */
  .form-section { margin-bottom:18px; }
  .fs-label { font-size:.7rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:8px; display:flex; align-items:center; justify-content:space-between; }
  .mf-input,.mf-select,.mf-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .mf-input:focus,.mf-select:focus,.mf-textarea:focus { border-color:#3b6ef8; background:white; }
  .mf-textarea { resize:vertical; min-height:52px; }
  .mf-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:4px; display:block; }
  .form-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:12px; }
  .form-row.single { grid-template-columns:1fr; }
  .q-list { display:flex; flex-direction:column; gap:8px; }
  .q-item { display:flex; align-items:center; gap:8px; background:#f8f9fc; border-radius:9px; padding:8px 10px; border:1px solid #e8ebf4; }
  .q-text { flex:1; font-size:.8rem; padding:5px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:white; font-family:'Sora',sans-serif; outline:none; }
  .q-text:focus { border-color:#3b6ef8; }
  .q-type-sel { font-family:'Sora',sans-serif; font-size:.72rem; padding:5px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:white; outline:none; min-width:90px; }
  .q-req { display:flex; align-items:center; gap:4px; font-size:.7rem; color:#9094a8; white-space:nowrap; }
  .q-del { width:26px; height:26px; border-radius:6px; border:none; background:#fef2f2; color:#ef4444; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .q-del svg { width:12px; height:12px; stroke-width:2.5; }
  .cls-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(130px,1fr)); gap:6px; }
  .cls-chip { display:flex; align-items:center; gap:6px; padding:7px 10px; border-radius:9px; border:1.5px solid #e0e4f0; cursor:pointer; font-size:.78rem; font-weight:600; color:#6b7194; background:#f8f9fc; user-select:none; transition:.15s; }
  .cls-chip.on { border-color:#3b6ef8; background:#eef2ff; color:#3b6ef8; }
  .cls-chk { width:16px; height:16px; border-radius:4px; border:2px solid #d0d5e8; background:white; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
  .cls-chip.on .cls-chk { background:#3b6ef8; border-color:#3b6ef8; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:20px; flex-wrap:wrap; }
  @media (max-width:600px) { .forms-grid{grid-template-columns:1fr;} .af-modal{padding:18px;} .form-row{grid-template-columns:1fr;} .cls-grid{grid-template-columns:1fr 1fr;} .rating-wrap{flex-direction:column;} }
`;

// ── Star SVG ──
const StarSVG = ({ filled, size=14 }) => (
  <svg className="star-svg" width={size} height={size} viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      fill={filled ? '#f59e0b' : '#e0e4f0'} stroke="none"/>
  </svg>
);

const Stars = ({ rating }) => (
  <div className="stars-row">
    {[1,2,3,4,5].map(i => <StarSVG key={i} filled={i <= Math.round(rating)} />)}
  </div>
);

const scoreColor = avg => avg >= 4 ? '#10c98e' : avg >= 3 ? '#f59e0b' : '#ef4444';
const scoreBorder = avg => avg >= 4 ? '#10c98e' : avg >= 3 ? '#f59e0b' : '#ef4444';

// ── Rating Question Chart ──
const RatingChart = ({ agg, color='#3b6ef8' }) => {
  if (!agg || agg.avg == null) return <div style={{fontSize:'.74rem',color:'#c0c5dc'}}>No responses yet</div>;
  const maxCount = Math.max(...[5,4,3,2,1].map(s => agg.distribution?.[s]||0), 1);
  return (
    <div className="rating-wrap">
      <div className="avg-block">
        <div className="avg-num" style={{color}}>{agg.avg}</div>
        <Stars rating={agg.avg}/>
        <div className="avg-label">/ 5.0</div>
        <div className="resp-label">{agg.count} resp.</div>
      </div>
      <div className="rating-bars">
        {[5,4,3,2,1].map(star => {
          const count = agg.distribution?.[star]||0;
          const pct   = agg.count > 0 ? (count/agg.count)*100 : 0;
          return (
            <div key={star} className="rb-row">
              <span className="rb-label">{star}</span>
              <div className="rb-track">
                <div className="rb-fill" style={{width:`${pct}%`, background: star>=4?'#10c98e':star===3?'#f59e0b':'#ef4444'}}/>
              </div>
              <span className="rb-count">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Yes/No Chart ──
const YNChart = ({ agg }) => {
  if (!agg) return null;
  const total = agg.count || 0;
  const yesPct = total > 0 ? (agg.yes/total)*100 : 0;
  const noPct  = total > 0 ? (agg.no/total)*100  : 0;
  return (
    <div className="yn-display">
      <div className="yn-bar-wrap">
        <div className="yn-row"><span className="yn-label yn-yes-label">Yes</span><div className="yn-track"><div className="yn-fill-yes" style={{width:`${yesPct}%`}}/></div><span className="yn-count">{agg.yes}</span></div>
        <div className="yn-row"><span className="yn-label yn-no-label">No</span><div className="yn-track"><div className="yn-fill-no"  style={{width:`${noPct}%`}}/></div><span className="yn-count">{agg.no}</span></div>
      </div>
      <span style={{fontSize:'.72rem',color:'#9094a8'}}>({total} resp.)</span>
    </div>
  );
};

// ── Question Results block ──
const QuestionResults = ({ questions, aggregate, evalType }) => {
  const color = evalType === 'class_teacher' ? '#0d9488' : '#3b6ef8';
  return (
    <div>
      {questions.map(q => {
        const agg = aggregate[q._id];
        if (!agg) return null;
        const isLowRating = q.type==='rating' && agg.avg != null && agg.avg < 3;
        const isGood      = q.type==='rating' && agg.avg != null && agg.avg >= 4;
        return (
          <div key={q._id} className="q-result">
            <div className="q-result-text">
              {q.text}
              {isLowRating && <span className="improve-tag">⚠ Needs Improvement</span>}
              {isGood      && <span className="good-tag">✓ Good</span>}
            </div>
            {q.type==='rating'  && <RatingChart agg={agg} color={color}/>}
            {q.type==='yes_no'  && <YNChart agg={agg}/>}
            {q.type==='text' && agg.texts?.length > 0 && (
              <div className="comments-list">
                {agg.texts.map((t,i) => <div key={i} className="comment-item">"{t}"</div>)}
              </div>
            )}
            {q.type==='text' && !agg.texts?.length && <div style={{fontSize:'.74rem',color:'#c0c5dc'}}>No comments</div>}
          </div>
        );
      })}
    </div>
  );
};

export default function AdminFeedback({ readOnly=false }) {
  const [forms,      setForms]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [classes,    setClasses]    = useState([]);
  const [stats,      setStats]      = useState({});
  const [createModal,setCreateModal]= useState(false);
  const [reportModal,setReportModal]= useState(null);
  const [sending,    setSending]    = useState(null); // teacherId being sent
  const [saving,     setSaving]     = useState(false);
  const [activeTeacherTab, setActiveTeacherTab] = useState({}); // {groupKey: evalType}
  const [form, setForm] = useState({
    title:'', description:'', academicYear: curYear(),
    startDate:'', endDate:'', targetClassIds:[],
    subjectQuestions:      DEFAULT_SUBJECT_QUESTIONS.map((q,i)=>({...q,order:i})),
    classTeacherQuestions: DEFAULT_CT_QUESTIONS.map((q,i)=>({...q,order:i})),
    includeClassTeacher: true,
  });

  useEffect(() => {
    load();
    const classesEndpoint = readOnly ? '/coordinator/classes' : '/admin/classes';
    api.get(classesEndpoint).then(r => setClasses(r.data?.classes||r.data||[])).catch(()=>{});
  }, []);

  useEffect(() => {
    if (createModal || reportModal) { document.documentElement.style.overflow='hidden'; document.body.style.overflow='hidden'; }
    else { document.documentElement.style.overflow=''; document.body.style.overflow=''; }
    return () => { document.documentElement.style.overflow=''; document.body.style.overflow=''; };
  }, [createModal, reportModal]);

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = readOnly ? '/coordinator/feedback' : '/admin/feedback';
      const { data } = await api.get(endpoint);
      setForms(data);
      if (readOnly) {
        setStats(Object.fromEntries(data.map(f => [f._id, f.stats])));
      } else {
        data.forEach(async f => {
          try { const { data: s } = await api.get(`/admin/feedback/${f._id}/stats`); setStats(p => ({...p,[f._id]:s})); } catch {}
        });
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const openReport = async (formId) => {
    try {
      const endpoint = readOnly ? `/coordinator/feedback/${formId}/report` : `/admin/feedback/${formId}/report`;
      const { data } = await api.get(endpoint);
      // sentToTeachers comes from report response (both admin and coordinator include it)
      const formData = forms.find(f => f._id === formId);
      setReportModal({ ...data, sentToTeachers: data.sentToTeachers || formData?.sentToTeachers || [] });
      setActiveTeacherTab({});
    } catch { toast.error('Failed to load report'); }
  };

  const handleSendToTeacher = async (formId, teacherId) => {
    setSending(teacherId);
    try {
      await api.put(`/admin/feedback/${formId}/send-to-teacher`, { teacherId });
      toast.success('Results sent to teacher');
      // Update local state
      setReportModal(p => ({
        ...p,
        sentToTeachers: [
          ...(p.sentToTeachers||[]).filter(s => s.teacher !== teacherId),
          { teacher: teacherId, sentAt: new Date() }
        ]
      }));
      load();
    } catch { toast.error('Failed to send'); }
    finally { setSending(null); }
  };

  const handleRequestSend = async (formId, teacherId, teacherName) => {
    setSending(teacherId);
    try {
      await api.post('/coordinator/feedback/request/feedback-send', { formId, teacherId, teacherName });
      toast.success('Request submitted — admin will send results to teacher');
    } catch { toast.error('Failed to submit request'); }
    finally { setSending(null); }
  };

  const handleActivate = async (id) => {
    try { await api.put(`/admin/feedback/${id}/activate`); toast.success('Campaign activated'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleClose = async (id) => {
    if (!window.confirm('Close this campaign?')) return;
    try { await api.put(`/admin/feedback/${id}/close`); toast.success('Closed'); load(); }
    catch { toast.error('Failed'); }
  };

  const handleSave = async () => {
    if (!form.title || !form.startDate || !form.endDate) return toast.error('Title and dates are required');
    if (!form.subjectQuestions.length) return toast.error('Add at least one subject question');
    setSaving(true);
    try {
      await api.post('/admin/feedback', {
        title: form.title, description: form.description, academicYear: form.academicYear,
        targetClasses: form.targetClassIds.map(id => ({ class: id, sections: [] })),
        subjectQuestions: form.subjectQuestions.filter(q=>q.text.trim()),
        classTeacherQuestions: form.classTeacherQuestions.filter(q=>q.text.trim()),
        includeClassTeacher: form.includeClassTeacher,
        startDate: form.startDate, endDate: form.endDate,
      });
      toast.success('Campaign created');
      setCreateModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleClass = id => { const s=String(id); setForm(p=>({...p,targetClassIds:p.targetClassIds.includes(s)?p.targetClassIds.filter(x=>x!==s):[...p.targetClassIds,s]})); };
  const addQ  = type => { const k=type==='subject'?'subjectQuestions':'classTeacherQuestions'; setForm(p=>({...p,[k]:[...p[k],{text:'',type:'rating',required:true,order:p[k].length}]})); };
  const updQ  = (type,i,f,v) => { const k=type==='subject'?'subjectQuestions':'classTeacherQuestions'; setForm(p=>{const qs=[...p[k]];qs[i]={...qs[i],[f]:v};return{...p,[k]:qs};}); };
  const remQ  = (type,i) => { const k=type==='subject'?'subjectQuestions':'classTeacherQuestions'; setForm(p=>{const qs=[...p[k]];qs.splice(i,1);return{...p,[k]:qs};}); };

  // Group report data by teacher, then by evalType
  const groupByTeacher = (groups) => {
    const map = {};
    for (const g of groups) {
      const tid = g.teacher._id;
      if (!map[tid]) map[tid] = { teacher: g.teacher, evalTypes: {} };
      map[tid].evalTypes[g.evalType] = g;
    }
    return Object.values(map);
  };

  // Compute overall avg rating for a group
  const overallAvg = (group, questions) => {
    const ratings = [];
    for (const q of questions) {
      if (q.type !== 'rating') continue;
      const agg = group.aggregate[q._id];
      if (agg?.avg != null) ratings.push(agg.avg);
    }
    return ratings.length ? Math.round((ratings.reduce((s,r)=>s+r,0)/ratings.length)*10)/10 : null;
  };

  const QEditor = ({ type, questions }) => (
    <div className="q-list">
      {questions.map((q,i) => (
        <div key={i} className="q-item">
          <input className="q-text" placeholder="Question text…" value={q.text} onChange={e=>updQ(type,i,'text',e.target.value)}/>
          <select className="q-type-sel" value={q.type} onChange={e=>updQ(type,i,'type',e.target.value)}>
            <option value="rating">⭐ Rating</option>
            <option value="yes_no">✓ Yes/No</option>
            <option value="text">📝 Text</option>
          </select>
          <label className="q-req"><input type="checkbox" checked={q.required} onChange={e=>updQ(type,i,'required',e.target.checked)}/>Req</label>
          <button className="q-del" onClick={()=>remQ(type,i)}><XMarkIcon/></button>
        </div>
      ))}
      <button className="btn btn-ghost btn-sm" style={{alignSelf:'flex-start',marginTop:2}} onClick={()=>addQ(type)}><PlusIcon/>Add Question</button>
    </div>
  );

  return (
    <div className="af"><style>{css}</style>
      <div className="af-hdr">
        <div><div className="af-title">Teacher Feedback & Evaluation</div><div className="af-sub">Create campaigns, view per-teacher reports and send results</div></div>
        {!readOnly && <button className="btn btn-blue" onClick={()=>setCreateModal(true)}><PlusIcon/>New Campaign</button>}
      </div>

      {loading ? <div className="af-loading"><div className="spinner"/>Loading…</div>
      : forms.length===0 ? (
        <div className="af-empty">
          <DocumentTextIcon style={{width:32,height:32,margin:'0 auto 12px',display:'block',opacity:.3}}/>
          No feedback campaigns yet.{!readOnly&&<><br/><button className="btn btn-blue btn-sm" style={{marginTop:12}} onClick={()=>setCreateModal(true)}><PlusIcon/>Create First Campaign</button></>}
        </div>
      ) : (
        <div className="forms-grid">
          {forms.map(f => {
            const s=stats[f._id]; const pct=s?.total>0?s.pct:0;
            return (
              <div key={f._id} className="form-card">
                <div className="fc-top">
                  <div><div className="fc-title">{f.title}</div><div className="fc-year">{f.academicYear}</div></div>
                  <span className="status-pill" style={{background:STATUS_BG[f.status],color:STATUS_COLOR[f.status]}}>{f.status}</span>
                </div>
                <div className="fc-meta">
                  <span>📅 {fmtDate(f.startDate)} — {fmtDate(f.endDate)}</span>
                  <span>🏫 {f.targetClasses?.length||0} class{f.targetClasses?.length!==1?'es':''}</span>
                  {s&&<span><span className="fc-stat">{s.submitted}</span>/{s.total} submitted</span>}
                </div>
                {s?.total>0&&<div className="progress-bar"><div className="progress-fill" style={{width:`${pct}%`}}/></div>}
                <div className="fc-actions">
                  {!readOnly&&f.status==='draft'&&<button className="btn btn-green btn-sm" onClick={()=>handleActivate(f._id)}><PlayIcon/>Activate</button>}
                  {!readOnly&&f.status==='active'&&<button className="btn btn-amber btn-sm" onClick={()=>handleClose(f._id)}><StopIcon/>Close</button>}
                  <button className="btn btn-ghost btn-sm" onClick={()=>openReport(f._id)}><EyeIcon/>Report</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Report Modal ── */}
      {reportModal && createPortal(
        <div className="af-backdrop" onClick={()=>setReportModal(null)}>
          <div className="af-modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <div className="modal-title">📊 {reportModal.form.title}</div>
                <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:2}}>{reportModal.form.academicYear} · {reportModal.totalSubmitted} total submissions</div>
              </div>
              <button className="modal-close" onClick={()=>setReportModal(null)}><XMarkIcon/></button>
            </div>

            {reportModal.groups.length===0 ? (
              <div style={{textAlign:'center',padding:'2rem',color:'#9094a8'}}>No submissions yet.</div>
            ) : (
              groupByTeacher(reportModal.groups).map(({ teacher, evalTypes }) => {
                const hasBoth    = evalTypes['subject'] && evalTypes['class_teacher'];
                const tabKey     = teacher._id;
                const activeTab  = activeTeacherTab[tabKey] || (evalTypes['subject'] ? 'subject' : 'class_teacher');
                const activeGroup= evalTypes[activeTab];
                const questions  = activeTab==='class_teacher' ? reportModal.form.classTeacherQuestions : reportModal.form.subjectQuestions;
                const avg        = activeGroup ? overallAvg(activeGroup, questions) : null;
                const isSent     = reportModal.sentToTeachers?.some(s => s.teacher?.toString()===teacher._id?.toString() || s.teacher===teacher._id);

                return (
                  <div key={teacher._id} className="teacher-section">
                    <div className="ts-head">
                      <div>
                        <div className="ts-name">{teacher.name}</div>
                        <div className="ts-meta">
                          {evalTypes['subject'] && `📚 ${evalTypes['subject'].subject} · ${evalTypes['subject'].className}`}
                          {hasBoth && ' · '}
                          {evalTypes['class_teacher'] && `🏠 Class Teacher`}
                          {' · '}{(evalTypes['subject']?.responses?.length||0)+(evalTypes['class_teacher']?.responses?.length||0)} responses
                        </div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                        {avg != null && (
                          <div className="score-ring" style={{borderColor:scoreBorder(avg),color:scoreColor(avg)}}>
                            {avg}
                          </div>
                        )}
                        {isSent
                          ? <span className="ts-sent-badge">✓ Sent</span>
                          : readOnly
                          ? (
                            <button className="btn btn-ghost btn-sm"
                              disabled={sending===teacher._id}
                              onClick={()=>handleRequestSend(reportModal.form._id, teacher._id, teacher.name)}>
                              <PaperAirplaneIcon/>{sending===teacher._id?'Requesting…':'Request Send'}
                            </button>
                          )
                          : (
                            <button className="btn btn-teal btn-sm"
                              disabled={sending===teacher._id}
                              onClick={()=>handleSendToTeacher(reportModal.form._id, teacher._id)}>
                              <PaperAirplaneIcon/>{sending===teacher._id?'Sending…':'Send to Teacher'}
                            </button>
                          )
                        }
                      </div>
                    </div>

                    <div className="ts-body">
                      {/* Eval type tabs */}
                      {hasBoth && (
                        <div className="eval-tabs">
                          <button className={`eval-tab${activeTab==='class_teacher'?' active-class_teacher':''}`}
                            onClick={()=>setActiveTeacherTab(p=>({...p,[tabKey]:'class_teacher'}))}>
                            🏠 Class Teacher Evaluation
                          </button>
                          <button className={`eval-tab${activeTab==='subject'?' active-subject':''}`}
                            onClick={()=>setActiveTeacherTab(p=>({...p,[tabKey]:'subject'}))}>
                            📚 Subject Evaluation
                            {evalTypes['subject']?.subject && ` (${evalTypes['subject'].subject})`}
                          </button>
                        </div>
                      )}
                      {!hasBoth && evalTypes['subject'] && (
                        <div style={{fontSize:'.72rem',fontWeight:600,color:'#3b6ef8',marginBottom:10}}>
                          📚 Subject Evaluation — {evalTypes['subject'].subject}
                        </div>
                      )}
                      {!hasBoth && evalTypes['class_teacher'] && (
                        <div style={{fontSize:'.72rem',fontWeight:600,color:'#0d9488',marginBottom:10}}>
                          🏠 Class Teacher Evaluation
                        </div>
                      )}

                      {activeGroup && (
                        <QuestionResults
                          questions={questions}
                          aggregate={activeGroup.aggregate}
                          evalType={activeTab}
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}

            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setReportModal(null)}>Close</button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* ── Create Campaign Modal ── */}
      {createModal && createPortal(
        <div className="af-backdrop" onClick={()=>setCreateModal(false)}>
          <div className="af-modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">New Feedback Campaign</div>
              <button className="modal-close" onClick={()=>setCreateModal(false)}><XMarkIcon/></button>
            </div>
            <div className="form-section">
              <div className="form-row">
                <div><label className="mf-label">Campaign Title *</label><input className="mf-input" placeholder="e.g. End of Term Evaluation" value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
                <div><label className="mf-label">Academic Year</label><input className="mf-input" value={form.academicYear} onChange={e=>setForm(p=>({...p,academicYear:e.target.value}))} placeholder="2025-2026"/></div>
                <div><label className="mf-label">Start Date *</label><input type="date" className="mf-input" value={form.startDate} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}/></div>
                <div><label className="mf-label">End Date *</label><input type="date" className="mf-input" value={form.endDate} onChange={e=>setForm(p=>({...p,endDate:e.target.value}))}/></div>
              </div>
              <div className="form-row single"><div><label className="mf-label">Description</label><textarea className="mf-textarea" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Brief description for students…"/></div></div>
            </div>
            <div className="form-section">
              <div className="fs-label">Target Classes</div>
              <div className="cls-grid">
                {classes.map(c=>{const on=form.targetClassIds.includes(String(c._id));return(<div key={c._id} className={`cls-chip${on?' on':''}`} onClick={()=>toggleClass(c._id)}><div className="cls-chk">{on&&<svg viewBox="0 0 12 12" width="9" height="9"><polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2.2" fill="none"/></svg>}</div>{c.name}</div>);})}
              </div>
            </div>
            <div className="form-section">
              <div className="fs-label">Subject Evaluation Questions <span style={{fontWeight:400,color:'#9094a8',fontSize:'.68rem'}}>(for each subject teacher)</span></div>
              <QEditor type="subject" questions={form.subjectQuestions}/>
            </div>
            <div className="form-section">
              <div className="fs-label">
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  Class Teacher Questions
                  <label style={{display:'flex',alignItems:'center',gap:5,fontWeight:400,fontSize:'.72rem',color:'#6b7194',cursor:'pointer'}}>
                    <input type="checkbox" checked={form.includeClassTeacher} onChange={e=>setForm(p=>({...p,includeClassTeacher:e.target.checked}))}/>Include
                  </label>
                </div>
              </div>
              {form.includeClassTeacher && <QEditor type="classTeacher" questions={form.classTeacherQuestions}/>}
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setCreateModal(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSave} disabled={saving}>{saving?'Creating…':'Create Campaign'}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}